// Desk build only: Route Desk opens this app with
//   /dashboard?ordering_for=<CHE Odoo partner id>&t=<ms>
// so an employee ordering by phone does not have to search for the CHE.
//
// The login redirects (/dashboard -> /refresh -> /login ...) drop the query
// string, so the id is stashed in sessionStorage at app start and applied
// once an employee is signed in, through the same setOrderingFor saga the
// cart's own CHE picker uses (the server still validates the CHE).
// The id in the link is only a request: anything unexpected is ignored.

export const PENDING_ORDERING_FOR_KEY = "desk_pending_ordering_for";
// The same id, kept after the pending one is used up, plus how applying it
// ended: the cart blocks checkout until the CHE shown is this one
// (deskCheckoutBlock below). Both live in sessionStorage so a reload
// (/refresh does one) keeps them.
export const REQUESTED_ORDERING_FOR_KEY = "desk_requested_ordering_for";
export const ORDERING_FOR_OUTCOME_KEY = "desk_ordering_for_outcome";
const MAX_SEARCH_PAGES = 10;

// Returns the odoo id from a query string ("?ordering_for=4242&t=1"), or
// null when it is missing or not a plain number.
export const readOrderingForParam = (search) => {
  const value = new URLSearchParams(search || "").get("ordering_for");
  return value && /^\d+$/.test(value) ? value : null;
};

// Called once at app start with window.location.search. Only honoured
// inside a frame: the desk host's frame-ancestors lets only Route Desk embed
// it, so a plain link from elsewhere cannot switch an employee's CHE and
// empty their cart.
export const stashOrderingForFromUrl = (search, storage = window.sessionStorage, win = window) => {
  if (win.self === win.top) return;
  const odooId = readOrderingForParam(search);
  if (!odooId) return;
  storage.setItem(PENDING_ORDERING_FOR_KEY, odooId);
  storage.setItem(REQUESTED_ORDERING_FOR_KEY, odooId);
  storage.removeItem(ORDERING_FOR_OUTCOME_KEY);
  // A new opening from Route Desk: the re-choose counts start afresh.
  storage.removeItem(REAPPLY_KEY);
};

export const getRequestedOrderingFor = (storage = window.sessionStorage) =>
  storage.getItem(REQUESTED_ORDERING_FOR_KEY);

export const getOrderingForOutcome = (storage = window.sessionStorage) =>
  storage.getItem(ORDERING_FOR_OUTCOME_KEY);

const outcomeListeners = new Set();

// Records how applying the requested CHE ended ("set", "no-match",
// "failed", "not-employee"; see applyPendingOrderingFor) and tells the cart.
export const setOrderingForOutcome = (outcome, storage = window.sessionStorage) => {
  storage.setItem(ORDERING_FOR_OUTCOME_KEY, outcome);
  outcomeListeners.forEach((listener) => listener(outcome));
};

// Returns the unsubscribe function.
export const subscribeOrderingForOutcome = (listener) => {
  outcomeListeners.add(listener);
  return () => outcomeListeners.delete(listener);
};

export const hasPendingOrderingFor = (storage = window.sessionStorage) =>
  !!storage.getItem(PENDING_ORDERING_FOR_KEY);

// The CHE search matches on part of a name, phone or odoo id, so take only
// the row whose odoo_id is exactly the one asked for.
export const findExactChe = (results, odooId) =>
  (results || []).find((row) => String(row?.odoo_id) === String(odooId)) || null;

// Applies a stashed id. Returns what happened, for tests and logging:
//   "none"         nothing stashed
//   "waiting"      not signed in yet; the id is kept for after sign-in
//   "not-employee" a CHE is signed in; ordering-for does not apply
//   "no-match"     no CHE with exactly that odoo id
//   "failed"       the lookup request failed
//   "set"          setOrderingFor dispatched
// The stash is removed before the lookup so it is used at most once.
export const applyPendingOrderingFor = async ({
  user,
  isAuthenticated,
  storage = window.sessionStorage,
  fetchChes,
  setOrderingFor,
}) => {
  const odooId = storage.getItem(PENDING_ORDERING_FOR_KEY);
  if (!odooId) return "none";
  if (!isAuthenticated || !user) return "waiting";
  storage.removeItem(PENDING_ORDERING_FOR_KEY);
  if (user.contact_type !== "employee") return "not-employee";
  try {
    const che = await lookupExactChe({ odooId, user, fetchChes });
    if (!che) return "no-match";
    setOrderingFor(user, che);
    return "set";
  } catch (e) {
    return "failed";
  }
};

// The CHE row with exactly this odoo id, or null. The search is a partial
// match, 50 rows a page: walk pages until the exact id turns up, at most
// MAX_SEARCH_PAGES. Throws when a request fails.
export const lookupExactChe = async ({ odooId, user, fetchChes }) => {
  for (let page = 1; page <= MAX_SEARCH_PAGES; page++) {
    const response = await fetchChes({ search_term: odooId, company_id: user?.country?.id, page });
    const che = findExactChe(response?.data?.results, odooId);
    if (che) return che;
    if (!response?.data?.next) break;
  }
  return null;
};

// After the CHE is set, a profile answer requested earlier can still land
// and put the old CHE back into auth.ordering_for_data (what the cart's
// "Customer:" line shows). True when the cart's CHE and the shown CHE are
// both known and differ: the caller then re-reads the profile, so the
// screen follows the server rather than a stale answer. A null shown CHE is
// normal (the server clears ordering_for after an order) and is left alone.
export const shownCheDisagrees = (cartOrderingFor, shownOrderingFor) =>
  !!(cartOrderingFor?.odoo_id && shownOrderingFor?.odoo_id &&
    String(cartOrderingFor.odoo_id) !== String(shownOrderingFor.odoo_id));

// What the cart says instead of offering checkout, inside Route Desk's panel.
export const DESK_CHECKOUT_WAITING = "Waiting for the CHE from Route Desk…";
export const DESK_CHECKOUT_NOT_FOUND = "This CHE could not be found in HE Products. Close HE Products and tell your manager.";
export const DESK_CHECKOUT_FAILED = "Could not select the CHE. Close HE Products and open it again.";
export const DESK_CHECKOUT_NO_REQUEST = "Route Desk did not send a CHE. Close HE Products and open it again from the CHE.";

// Inside the panel an employee may check out only when both the cart's CHE
// and the CHE the server says it is ordering for (shown in the cart) are
// the one Route Desk asked for. There is no CHE picker in the panel, so a
// CHE left from an earlier session must never be ordered for. Returns null
// when checkout may go ahead, else the line to show instead.
//   orderInProgress  an order was already sent: its CHE is fixed, so the
//                    payment steps are not interrupted (the server clears
//                    its "ordering for" after an order)
//   posting          the CHE is being set right now
export const deskCheckoutBlock = ({
  inFrame, isEmployee, orderInProgress, requestedId, outcome, posting, cartCheId, shownCheId,
}) => {
  if (!inFrame || !isEmployee || orderInProgress) return null;
  if (!requestedId) return DESK_CHECKOUT_NO_REQUEST;
  if (outcome === "no-match") return DESK_CHECKOUT_NOT_FOUND;
  if (outcome === "failed") return DESK_CHECKOUT_FAILED;
  const same = (id) => id !== null && id !== undefined && String(id) === String(requestedId);
  if (same(cartCheId) && same(shownCheId)) return null;
  // Set, finished, and still not this CHE: the update failed, or the
  // server cleared it after an order. Reopening applies it again.
  if (outcome === "set" && !posting && (!same(cartCheId) || !shownCheId)) return DESK_CHECKOUT_FAILED;
  return DESK_CHECKOUT_WAITING;
};

// ---------------------------------------------------------------------------
// Choosing Route Desk's CHE again after the server cleared it (Thijs,
// 2026-10-15). The backend clears an employee's "ordering for" after a paid
// order or invoice (accounting/models.py and orders/models.py: `ordering_for
// = None`), so the panel then showed "Could not select the CHE. Close HE
// Products and open it again." Instead the panel applies the CHE Route Desk
// last asked for once more, through the same applyPendingOrderingFor /
// setOrderingFor path as the first time.
//
// Loop guard, kept per tab in sessionStorage:
//   { requestedId, nullSince, active, clears, tries, lastAt }
//   nullSince  when the server was first seen showing no CHE; a clear counts
//            only after REAPPLY_SETTLE_MS of that (a profile answer requested
//            before the CHE was set can land after it and show none for a
//            moment, until the re-read the set itself makes)
//   active   a clear is being recovered (until the server shows the CHE again)
//   clears   clears recovered for this requested CHE (at most REAPPLY_MAX_CLEARS)
//   tries    tries for the current clear (at most REAPPLY_MAX_TRIES_PER_CLEAR,
//            REAPPLY_RETRY_MS apart); after that the old message stays
export const REAPPLY_KEY = "desk_reapply_ordering_for";
export const REAPPLY_RETRY_MS = 30 * 1000;
export const REAPPLY_MAX_TRIES_PER_CLEAR = 2;
export const REAPPLY_MAX_CLEARS = 5;
export const REAPPLY_SETTLE_MS = 5 * 1000;

export const loadReapplyState = (storage = window.sessionStorage) => {
  try {
    const state = JSON.parse(storage.getItem(REAPPLY_KEY));
    return state && typeof state === "object" ? state : null;
  } catch (e) {
    return null;
  }
};

export const saveReapplyState = (state, storage = window.sessionStorage) => {
  try {
    storage.setItem(REAPPLY_KEY, JSON.stringify(state));
  } catch (e) {
    // Storage blocked: the caller's in-memory copy still limits this page.
  }
};

// What to do now. Returns { action: "none" | "reapply" | "wait", state, waitMs,
// gaveUp } (gaveUp: the tries are used up; the old message may show)
// where `state` is the guard to keep (unchanged for "none" unless a clear
// ended or the requested CHE changed).
//   mayApply    DeskAccountGuard confirmed the account (mayApplyOrderingFor)
//   outcome     how applying the requested CHE last ended; only "set" counts
//               (a lookup that failed keeps its own message)
//   shownCheId  the CHE the server says it is ordering for (null = cleared)
export const reapplyDecision = ({
  inFrame, mayApply, signedIn, isFetching, posting, requestedId, outcome, shownCheId, state, now,
}) => {
  if (!inFrame || !mayApply || !signedIn || isFetching || posting || !requestedId) return { action: "none", state };
  // Counts belong to one requested CHE: a new request starts afresh, and an
  // older request is never applied (only `requestedId` ever is).
  let s = state && String(state.requestedId) === String(requestedId)
    ? state
    : { requestedId: String(requestedId), nullSince: null, active: false, clears: 0, tries: 0, lastAt: null };
  const shown = shownCheId === null || shownCheId === undefined ? null : String(shownCheId);
  if (shown !== null) {
    const ended = shown === String(requestedId) && s.active ? { active: false, tries: 0 } : {};
    const next = { ...s, ...ended, nullSince: null };
    return { action: "none", state: next };
  }
  // "reselecting": this code is itself choosing the CHE again (see
  // DeskOrderingFor); a first lookup that failed keeps its own message.
  if (outcome !== "set" && outcome !== "reselecting") return { action: "none", state: s };
  if (!s.active) {
    if (s.clears >= REAPPLY_MAX_CLEARS) return { action: "none", state: s, gaveUp: true };
    if (!Number.isFinite(s.nullSince)) {
      return { action: "wait", state: { ...s, nullSince: now }, waitMs: REAPPLY_SETTLE_MS };
    }
    const seenFor = now - s.nullSince;
    if (seenFor < REAPPLY_SETTLE_MS) return { action: "wait", state: s, waitMs: REAPPLY_SETTLE_MS - seenFor };
    return { action: "reapply", state: { ...s, active: true, clears: s.clears + 1, tries: 1, lastAt: now } };
  }
  if (s.tries >= REAPPLY_MAX_TRIES_PER_CLEAR) return { action: "none", state: s, gaveUp: true };
  const waited = now - (s.lastAt || 0);
  if (waited < REAPPLY_RETRY_MS) return { action: "wait", state: s, waitMs: REAPPLY_RETRY_MS - waited };
  return { action: "reapply", state: { ...s, tries: s.tries + 1, lastAt: now } };
};

// One try: the cart's own CHE row when it is the requested one (no search),
// else the exact search; then the same setOrderingFor as on opening.
// Returns "set", "no-match" or "failed". Never sets another CHE than
// `requestedId`.
export const reapplyChe = async ({ requestedId, cartChe, user, fetchChes, setOrderingFor }) => {
  try {
    const che = cartChe && String(cartChe.odoo_id) === String(requestedId)
      ? cartChe
      : await lookupExactChe({ odooId: requestedId, user, fetchChes });
    if (!che || String(che.odoo_id) !== String(requestedId)) return "no-match";
    setOrderingFor(user, che);
    return "set";
  } catch (e) {
    return "failed";
  }
};
